/* Service and region presentation only. Shopify's native contact form owns submission. */
(() => {
  'use strict';
  if (window.KaliaFullService) return;
  const services = Object.freeze({showroom:'Showroom sample visit','in-home':'In-home consultation'});
  const localRegions = new Set(['MA','CT','NH','RI']);
  const validRegion = value => localRegions.has(value) || value === 'OTHER';
  const instances = new WeakMap();
  class KaliaFullService {
    static requested(search) {
      const values = new URLSearchParams(search).getAll('service');
      return values.length === 1 && Object.prototype.hasOwnProperty.call(services,values[0]) ? values[0] : null;
    }
    constructor(root) {
      this.root = root;
      this.options = root.querySelector('[data-service-options]');
      this.choices = [...root.querySelectorAll('[data-service-option]')];
      this.finder = root.querySelector('[data-service-finder]');
      this.regions = [...root.querySelectorAll('[data-service-region]')];
      if ((!this.options || !this.choices.length) && !this.finder) return;
      this.location = root.querySelector('[data-service-location]');
      this.locationInput = root.querySelector('[data-service-location-input]');
      this.addressInputs = [...new Set([...root.querySelectorAll('[data-service-address-input]'),...(this.locationInput ? [this.locationInput] : [])])];
      this.requiredMarks = [...root.querySelectorAll('[data-service-location-required]')];
      if (!this.requiredMarks.length) {
        const mark = root.querySelector('[data-service-location-required]');
        if (mark) this.requiredMarks.push(mark);
      }
      this.addressControls = new Set([...(this.location?.querySelectorAll('input, select, textarea, button, fieldset') || []),...this.addressInputs]);
      if (this.location?.matches('fieldset')) this.addressControls.add(this.location);
      this.request = root.querySelector('[data-service-request]');
      this.confirmRegion = root.querySelector('[data-service-confirm-region]');
      this.regionSummary = root.querySelector('[data-service-region-summary]');
      this.localContent = root.querySelector('[data-service-local-content]');
      this.pending = root.querySelector('[data-service-region-pending]');
      this.localMessage = root.querySelector('[data-service-region-local]');
      this.diy = root.querySelector('[data-service-region-diy]');
      this.success = Boolean(root.querySelector('[data-service-success]'));
      this.hasError = Boolean(root.querySelector('.kfsrv-error, [data-service-error]'));
      this.gated = Boolean(this.finder && this.regions.length && this.localContent);
      this.localWasHidden = this.localContent?.hidden;
      this.controls = new Map([...new Set([...(this.localContent?.querySelectorAll('input, select, textarea, button, fieldset') || []),...this.addressControls])].map(control => [control,control.disabled]));
      this.localEnabled = true;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      if (this.options?.dataset.serviceRestored !== 'true' && !this.hasError) this.choose(this.constructor.requested(window.location.search));
      this.update();
      this.choices.forEach(choice => choice.addEventListener('change',() => this.update(),options));
      this.regions.forEach(radio => radio.addEventListener('change',() => {
        if (radio.checked && validRegion(radio.value)) this.chooseRegion(radio.value);
      },options));
      this.confirmRegion?.addEventListener('change',() => this.chooseRegion(validRegion(this.confirmRegion.value) ? this.confirmRegion.value : ''),options);
      const restored = this.confirmRegion?.value;
      const checked = this.regions.find(radio => radio.checked && validRegion(radio.value))?.value;
      this.chooseRegion(validRegion(restored) ? restored : checked || '');
      this.resolveInitialAnchor();
      root.querySelectorAll('[data-service-choice]').forEach(link => link.addEventListener('click',event => {
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button !== undefined && event.button !== 0)) return;
        if (!this.choose(link.dataset.serviceChoice)) return;
        event.preventDefault();
        this.moveTo(this.request,this.choices.find(choice => choice.checked));
      },options));
    }
    choose(key) {
      if (!Object.prototype.hasOwnProperty.call(services,key)) return false;
      const option = this.choices.find(item => item.value === services[key]);
      if (!option) return false;
      this.choices.forEach(choice => { choice.checked = choice === option; });
      this.update(); return true;
    }
    chooseRegion(value) {
      this.region = validRegion(value) ? value : '';
      this.regions.forEach(radio => { radio.checked = radio.value === this.region; });
      if (this.confirmRegion) this.confirmRegion.value = this.region;
      if (this.regionSummary) this.regionSummary.value = this.region;
      if (this.pending) this.pending.hidden = Boolean(this.region);
      if (this.localMessage) this.localMessage.hidden = !this.region;
      if (this.diy) this.diy.hidden = this.region !== 'OTHER';
      // Project location informs the request; it never blocks a showroom visit.
      if (this.localContent) this.localContent.hidden = false;
      this.localEnabled = true;
      this.updateDisabled();
    }
    update() {
      this.inHome = this.choices.some(choice => choice.checked && choice.value === services['in-home']);
      if (this.location) this.location.hidden = !this.inHome;
      this.addressInputs.forEach(input => { input.required = this.inHome; input.setAttribute('aria-required',String(this.inHome)); });
      this.requiredMarks.forEach(mark => { mark.hidden = !this.inHome; });
      this.updateDisabled();
    }
    updateDisabled() {
      this.controls.forEach((disabled,control) => {
        control.disabled = disabled || !this.localEnabled || (!this.inHome && this.addressControls.has(control));
      });
    }
    resolveInitialAnchor() {
      const pending = () => !this.success && !this.hasError && window.location.hash === '#service-request';
      if (!pending()) return;
      const reveal = () => {
        if (!this.controller.signal.aborted && pending()) this.moveTo(this.request,this.choices.find(choice => choice.checked));
      };
      if (window.requestAnimationFrame) this.anchorFrame = window.requestAnimationFrame(reveal);
      else reveal();
    }
    moveTo(section,focus) {
      section?.scrollIntoView({behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth',block:'start'});
      focus?.focus({preventScroll:true});
    }
    destroy() {
      this.controller?.abort();
      if (this.anchorFrame !== undefined) window.cancelAnimationFrame?.(this.anchorFrame);
      this.controls?.forEach((disabled,control) => { control.disabled = disabled; });
      if (this.localContent) this.localContent.hidden = this.localWasHidden;
    }
  }
  window.KaliaFullService = KaliaFullService;
  const roots = target => [...(target.matches?.('[data-full-service]') ? [target] : []),...target.querySelectorAll('[data-full-service]')];
  const start = target => roots(target).forEach(root => { if (!instances.has(root)) instances.set(root,new KaliaFullService(root)); });
  document.addEventListener('shopify:section:load',event => start(event.target));
  document.addEventListener('shopify:section:unload',event => roots(event.target).forEach(root => { instances.get(root)?.destroy(); instances.delete(root); }));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => start(document),{once:true}); else start(document);
})();
